import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { API_BASE_URL } from '../api-base-url.token';

/* ============================================================
 * DTOs alignés avec OrganisationsController (backend)
 * ============================================================ */
export interface OrganisationDetailsDto {
  id: string;
  name: string;
  slug: string;
  has_logo?: boolean;
  logo_updated_at?: string | null;
  prescripteur_search_enabled?: boolean;
}

export interface UpdateOrganisationSettingsPayload {
  prescripteur_search_enabled?: boolean;
}

/* ============================================================
 * Service HTTP
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class OrganisationsService {
  private http = inject(HttpClient);

  // supporte '', '/', 'https://api', 'https://api/'
  private base = inject(API_BASE_URL).replace(/\/+$/, '');

  private join(path: string): string {
    if (!this.base) return path; // même origine (dev proxy)
    return `${this.base}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  /** Détails d'organisation partagés entre le shell et les pages (site, etc.) qui les demandent
   *  indépendamment. Invalidé sur upload/suppression de logo. */
  private detailsCache = new Map<string, Observable<OrganisationDetailsDto>>();

  /** GET détails d’une organisation */
  getDetails(id: string): Observable<OrganisationDetailsDto> {
    if (!this.detailsCache.has(id)) {
      this.detailsCache.set(
        id,
        this.http.get<OrganisationDetailsDto>(this.join(`/api/v1/organisations/${id}`)).pipe(shareReplay(1))
      );
    }
    return this.detailsCache.get(id)!;
  }

  /** POST upload du logo, partagé par tous les sites de l’organisation (max 10 Mo) */
  uploadLogo(id: string, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<void>(this.join(`/api/v1/organisations/${id}/logo`), formData)
      .pipe(tap(() => this.detailsCache.delete(id)));
  }

  /** PUT paramètres d'organisation (ex: activation du champ de recherche carte prescripteur) */
  updateSettings(id: string, payload: UpdateOrganisationSettingsPayload): Observable<void> {
    return this.http.put<void>(this.join(`/api/v1/organisations/${id}/settings`), payload)
      .pipe(tap(() => this.detailsCache.delete(id)));
  }

  /** DELETE suppression du logo */
  deleteLogo(id: string): Observable<void> {
    return this.http.delete<void>(this.join(`/api/v1/organisations/${id}/logo`))
      .pipe(tap(() => this.detailsCache.delete(id)));
  }

  /** URL publique du logo (pas d'auth requise), avec cache-busting sur logo_updated_at */
  getLogoUrl(id: string, logoUpdatedAt?: string | null): string {
    const url = this.join(`/public/v1/organisations/${id}/logo`);
    return logoUpdatedAt ? `${url}?v=${encodeURIComponent(logoUpdatedAt)}` : url;
  }
}
